import {
  Sparkles,
  Gem,
  Award,
  Crown,
  Coins,
  type LucideIcon,
} from 'lucide-react';
import React from 'react';

import { getPackById } from '../config/creditPacks';
import type { PlanKey } from '../hooks/usePlanStatus';

export type PlanVisualKey = PlanKey;

export interface PlanVisual {
  key: PlanVisualKey;
  label: string;
  periodLabel: string;
  Icon: LucideIcon;
  iconClassName: string;
}

function periodLabelForPlanId(planId: string | null): string {
  const pack = planId ? getPackById(planId) : undefined;
  if (pack?.periodLabel) {
    return pack.periodLabel.replace(/^\//, '').trim();
  }
  if (!planId) return 'trial credits';
  return 'per billing period';
}

const PLAN_ICON: Record<PlanVisualKey, { Icon: LucideIcon; iconClassName: string }> = {
  free: { Icon: Sparkles, iconClassName: 'text-slate-500' },
  monthly: { Icon: Gem, iconClassName: 'text-blue-600' },
  quarterly: { Icon: Award, iconClassName: 'text-emerald-600' },
  halfyearly: { Icon: Crown, iconClassName: 'text-teal-600' },
  legacy: { Icon: Crown, iconClassName: 'text-amber-600' },
};

export function getPlanVisual(
  planId: string | null,
  planKey: PlanKey,
  planDisplayName: string,
): PlanVisual {
  const { Icon, iconClassName } = PLAN_ICON[planKey];
  return {
    key: planKey,
    label: planKey === 'free' ? 'Free' : planDisplayName,
    periodLabel: planKey === 'free' ? 'trial credits' : periodLabelForPlanId(planId),
    Icon,
    iconClassName,
  };
}

interface PlanIconProps {
  planId: string | null;
  planKey: PlanKey;
  className?: string;
  size?: number;
}

export function PlanIcon({ planKey, className = '', size = 16 }: PlanIconProps) {
  const { Icon, iconClassName } = PLAN_ICON[planKey];
  return <Icon size={size} className={`shrink-0 ${iconClassName} ${className}`} aria-hidden />;
}

export function CreditsRowIcon({ size = 16, className = '' }: { size?: number; className?: string }) {
  return <Coins size={size} className={`shrink-0 text-amber-500 ${className}`} aria-hidden />;
}
